import React, { useEffect, useState } from "react";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import { Button } from "melodies-source/Button";
import { useNavigate } from "react-router-dom";
import { Caption } from "melodies-source/Text";
import { TextInput } from "melodies-source/TextInput";
import { SvgMail } from "melodies-source/Svgs/Mail";
import logoMax from "Components/Assets/logoMax.png";
import styled from "styled-components";
import LoginBackgroundImg from "Components/Assets/loginBgImg.jpg";
import { useUser } from "reactfire";
import Countdown from "react-countdown";

const provider = new GoogleAuthProvider();

const RESEND_SECONDS = 60;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (value: string) => EMAIL_REGEX.test(value.trim());

export const Login = () => {
  const auth = getAuth();
  const { data: user, status } = useUser();
  const navigate = useNavigate();

  const [view, setView] = useState<"default" | "magic">("default");
  const [signingIn, setSigningIn] = useState(() =>
    isSignInWithEmailLink(auth, window.location.href),
  );
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendAt, setResendAt] = useState<number>(0);

  const signIn = () =>
    signInWithPopup(auth, provider)
      .then((result) => {
        const user = result.user;

        result.user
          .getIdTokenResult()
          .then((res) => console.log("claims:", res.claims));

        if (user) {
          navigate("/admin");
        }
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        console.log(errorCode, errorMessage);
      });

  const sendLink = async () => {
    if (!isValidEmail(email) || loading || sentTo) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/auth/magic-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }
      setSentTo(email.trim());
      setResendAt(Date.now() + RESEND_SECONDS * 1000);
    } catch (err) {
      console.log(err);
      setError("We couldn't send the link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const backToDefault = () => {
    setView("default");
    setSentTo(null);
    setError(null);
  };

  // Complete sign-in when the user returns via the emailed magic link
  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    const linkEmail = new URLSearchParams(window.location.search).get("email");
    if (!linkEmail) {
      setSigningIn(false);
      setView("magic");
      setError("That login link is missing an email address.");
      return;
    }
    signInWithEmailLink(auth, linkEmail, window.location.href)
      .then(() => navigate("/admin"))
      .catch((err) => {
        console.log(err?.code, err?.message);
        setSigningIn(false);
        setView("magic");
        setError("That login link is invalid or has expired.");
      });
  }, []);

  useEffect(() => {
    if (user && status === "success") {
      navigate("/admin");
    }
  }, [status]);

  const sendDisabled = loading || !isValidEmail(email) || Boolean(sentTo);

  return (
    <Container background={LoginBackgroundImg}>
      <Wrapper>
        <Logo src={logoMax} alt="" />

        {signingIn ? (
          <Prompt>Signing you in…</Prompt>
        ) : view === "default" ? (
          <>
            <Button variant="primary" onClick={() => signIn()}>
              Login
            </Button>
            <Caption>Access your account via Google</Caption>
            <LinkText onClick={() => setView("magic")}>
              Get a One-Time Login Link
            </LinkText>
          </>
        ) : (
          <>
            <Prompt>
              Enter your email address and we’ll send you a one-time login link.
            </Prompt>
            <FormColumn
              onSubmit={(e) => {
                e.preventDefault();
                sendLink();
              }}
            >
              <TextInput
                label="Email Address"
                placeholder="Email..."
                leftIcon={<SvgMail />}
                value={email}
                onChange={setEmail}
                isDisabled={loading || Boolean(sentTo)}
                hasError={Boolean(error)}
                helperText={error || undefined}
              />
              <Button type="submit" variant="primary" isDisabled={sendDisabled}>
                Send me a Login Link
              </Button>
            </FormColumn>
            <LinkText onClick={backToDefault}>Back</LinkText>

            {sentTo && (
              <ConfirmBox>
                <ConfirmText>
                  An email to access your account has been sent to{" "}
                  <strong>{sentTo}</strong>
                </ConfirmText>
                <ResendText>
                  <Countdown
                    key={resendAt}
                    date={resendAt}
                    onComplete={() => setSentTo(null)}
                    renderer={({ minutes, seconds, completed }) =>
                      completed ? null : (
                        <>
                          Didn’t get it? Resend in{" "}
                          {String(minutes).padStart(2, "0")}:
                          {String(seconds).padStart(2, "0")}
                        </>
                      )
                    }
                  />
                </ResendText>
              </ConfirmBox>
            )}
          </>
        )}
      </Wrapper>
    </Container>
  );
};

const Logo = styled.img`
  object-fit: cover;
  width: 160px;
`;
const Container = styled.div<{ background: string }>`
  display: grid;
  width: 100vw;
  height: 100vh;
  place-items: center;
  background-image: url(${({ background }) => background});
  background-size: cover;
`;
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 35%;
  min-width: 350px;
  max-width: 450px;
  background: #fff;
  border-radius: 6px;
  padding: 40px 30px;
  gap: 24px;
  button {
    width: 100%;
  }
  p {
    margin: 0;
  }
`;
const FormColumn = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 24px;
`;
const Prompt = styled.p`
  text-align: center;
  font-weight: 700;
  font-size: 20px;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.black80};
`;
const LinkText = styled.div`
  color: ${({ theme }) => theme.colors.navy};
  font-weight: 700;
  cursor: pointer;
  text-align: center;
`;
const ConfirmBox = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.gray3};
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: center;
`;
const ConfirmText = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.black80};
`;
const ResendText = styled.div`
  color: ${({ theme }) => theme.colors.black40};
  font-weight: 600;
`;
